import React from "react";
import { Modal as AntModal } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { getFormattedNumber } from "@utils";
import {
  PromoBonusModalCH,
  PromoBonusModalMM,
  PromoBonusModalEN,
  FirstTopUpBG,
  FirstTopUpReject,
  FirstTopUpAccept
} from '@assets';
import { localStorage } from "@utils";
import { ENGLISH_LANGUAGE_CODE_LK, BURMESE_LANGUAGE_CODE_LK } from "@constants";

const Modal = styled(AntModal)`
  padding: 0px !important;
  .ant-modal-title {
    text-align: center;
    color: ${(props) => props.theme.primaryColor};
  }
  .ant-modal-content {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .ant-modal-footer {
    text-align: center;
  }
`;

const Container = styled.div`
  position: absolute;
  top: 90px;
  left: 0;
  margin: 18px 0;
  height: 322px;
  width: 100%;

  @media screen and (max-width: 370px) {
    height: 272px;
    top: 80px;
  }
`;

const Title = styled.div`
opacity: ${props => props.hide === 'true' ? 0 : 1};
  padding-top: 14px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  color: #000000;
  text-align: center;

  @media screen and (max-width: 370px) {
    padding-top: 12px;
    font-size: 14px;
  }
`;

const LabelContainer = styled.div`
  padding-top: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const FirstLabel = styled.div`
  font-weight: 400;
  color: #2D1F04;
  width: 100%;
  font-size: 12px;
  text-align: center;
  line-height: 2;
  span {
    font-weight: bold;
  }
  @media screen and (max-width: 370px) {
    font-size: 10px;
  }
`;

const TopUpLabel = styled.div`
  font-weight: 400;
  color: #2D1F04;
  width: 100%;
  font-size: 12px;
  text-align: center;
  line-height: 2;
  span {
    font-weight: bold;
  }
  @media screen and (max-width: 370px) {
    font-size: 10px;
  }
`;

const TotalLabel = styled.div`
  font-weight: 400;
  color: #2D1F04;
  width: 100%;
  font-size: 12px;
  text-align: center;
  line-height: 2;
  span {
    font-weight: bold;
  }
  @media screen and (max-width: 370px) {
    font-size: 10px;
  }
`;

const RuleLabel = styled.div`
  padding-bottom: 13px;
  font-size: 10px;
  color: #916122;

  @media screen and (max-width: 370px) {
    font-size: 9px;
    padding-bottom: 8px;
  }
`;

const BtnWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  @media screen and (max-width: 370px) {
  }
`;

const CancelButton = styled.button`
  padding-top: 4px;
  background-image: url(${FirstTopUpReject});
	background-size: cover;
  background-repeat: no-repeat;
  width: 136px;
  height: 46px;
  color: #8D5B11;
  font-size: 14px;
  text-align: center;
  border: none;
  cursor: pointer;
  border-radius: 50px;

  @media screen and (max-width: 370px) {
    width: 110px;
    font-size: 12px;
  }
`;

const AcceptButton = styled.button`
  padding-top: 4px;
  background-image: url(${FirstTopUpAccept});
	background-size: cover;
  background-repeat: no-repeat;
  width: 136px;
  height: 46px;
  color: #000000;
  font-size: 14px;
  text-align: center;
  border: none;
  cursor: pointer;
  position: relative;
  border-radius: 50px;

  @media screen and (max-width: 370px) {
    width: 110px;
    font-size: 12px;
  }
`;

const LottieWrapper = styled.div`
  width: 350px;
  position: relative;

  @media screen and (max-width: 370px) {
    width: 300px;
  }
`;

const Bottom = styled.div`
  position: absolute;
  bottom: 1px;
  width: 100%;
  text-align: center;
`;

export const Image = styled.img.attrs({
  alt: "logo",
})`
	object-fit: contain;
  width: 350px;
  
  @media screen and (max-width: 370px) {
    width: 300px;
  }
`;

const FirstBonusConfirmModal = ({
  exceed_bonus_limit,
  total_amount,
  max_topup_reward,
  amount,
  bonus_percent,
  isPromoUser,
  open,
  onBonusAccept,
  handleClose,
}) => {
  const { t } = useTranslation();
  const userPreferedLanguage = localStorage.getUserPreferedLanguage();

  const calculateBonus = () => {
    return exceed_bonus_limit ? max_topup_reward : Math.ceil(amount * bonus_percent * 0.01);
  };

  const calculateTotal = () => {
    return exceed_bonus_limit ? total_amount : (amount + calculateBonus());
  };

  const getImage = () => {
    if (isPromoUser) {
      if (userPreferedLanguage === ENGLISH_LANGUAGE_CODE_LK) {
        return PromoBonusModalEN;
      } else if (userPreferedLanguage === BURMESE_LANGUAGE_CODE_LK) {
        return PromoBonusModalMM;
      } else {
        return PromoBonusModalCH;
      }
    } else {
      return FirstTopUpBG;
    }
  }

  return (
    <>
      <Modal centered destroyOnClose closable={false} open={open} footer={null}>
        <LottieWrapper>
          <Image src={getImage()} />
          <Container>
            <Title hide={isPromoUser ? 'true' : 'false'}>
              {t("wallet.firstTopUpBonusTitle")}
            </Title>
            <LabelContainer>
              <FirstLabel>
                {t("wallet.firstTopUpBonusPercentage1")}
                {" - "}
                <span>{bonus_percent}%</span>
                {" "}
                {t("wallet.firstTopUpBonusPercentage2")}
              </FirstLabel>
              <TopUpLabel>
                {t("wallet.firstTopUpBonusTopUpAndBonus1")}
                {" - "}
                <span>{getFormattedNumber(amount)}</span>
                {"၊ "}
                {t("wallet.firstTopUpBonusTopUpAndBonus2")}
                {" - "}
                <span>{getFormattedNumber(calculateBonus())}</span>
              </TopUpLabel>
              <TotalLabel>
                {t("wallet.firstTopUpBonusTotal1")}
                {" "}
                <span>{getFormattedNumber(calculateTotal())}</span>
                {" "}
                {t("wallet.firstTopUpBonusTotal2")}
              </TotalLabel>
              {exceed_bonus_limit &&
                <TotalLabel
                  isBurmese={userPreferedLanguage === BURMESE_LANGUAGE_CODE_LK ? "true" : 'false'}
                >
                  {`(${t("wallet.maxBonusAmount")} - `}
                  <span>{getFormattedNumber(max_topup_reward)}</span>
                  {`)`}
                </TotalLabel>
              }
            </LabelContainer>
            <Bottom>
              <RuleLabel>{t("wallet.firstTopUpBonusRule")}</RuleLabel>
              <BtnWrapper>
                <CancelButton onClick={handleClose}>
                  {t("wallet.reject")}
                </CancelButton>
                <AcceptButton onClick={onBonusAccept}>
                  {t("wallet.accept")}
                </AcceptButton>
              </BtnWrapper>
            </Bottom>
          </Container>
        </LottieWrapper>
      </Modal>
    </>
  );
};

export default FirstBonusConfirmModal;